import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { catchError, of } from "rxjs";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { ThemeFormComponent } from "../../components/theme-form/theme-form.component";
import { toThemeConfigPayload } from "../../models/theme.mapper";
import { FreeformConfigPayload, ThemeFormValue, UpdateThemeRequest } from "../../models/theme.model";
import { ThemesApiService } from "../../services/themes.api.service";

@Component({
    selector: "app-edit-theme",
    templateUrl: "./edit-theme.component.html",
    styleUrl: "./edit-theme.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ThemeFormComponent]
})
export class EditThemeComponent {
    private readonly themesService = inject(ThemesApiService);
    private readonly router = inject(Router);
    private readonly snackbarService = inject(SnackbarService);
    private readonly themeId = inject(ActivatedRoute).snapshot.paramMap.get("themeId") ?? "";

    // On load failure (bad id / network) surface an error and return to the list rather
    // than throwing during change detection; the template stays on "Loading" until then.
    readonly theme = toSignal(
        this.themesService.getTheme(this.themeId).pipe(
            catchError(() => {
                this.snackbarService.show("We couldn’t load this theme. Try again.", "error");
                void this.router.navigate(["/admin/themes"]);
                return of(null);
            })
        ),
        { initialValue: null }
    );

    // In-flight guard so the primary can't double-submit. TODO: fold into a fuller
    // submitting/loading treatment (e.g. spinner) when that's designed.
    readonly submitting = signal(false);

    onSubmit(value: ThemeFormValue): void {
        this.submitting.set(true);
        this.themesService.updateTheme(this.themeId, this.buildUpdate(value)).subscribe({
            next: () => {
                this.submitting.set(false);
                this.snackbarService.show("Theme saved.", "success");
            },
            error: () => {
                this.submitting.set(false);
                this.snackbarService.show("We couldn’t save your changes. Try again.", "error");
            }
        });
    }

    // The update endpoint replaces the whole theme, so anything this form doesn't manage has to be
    // round-tripped or it's nulled out. themeConfig is merged (the Display and PWA tabs own only
    // part of it), and segments are carried through untouched since no tab edits them.
    //
    // aiConfig needs no merge: the AI tabs are seeded from the loaded config, so the form's value
    // already carries it — and sending that verbatim is what lets the user clear the section.
    private buildUpdate(value: ThemeFormValue): UpdateThemeRequest {
        const current = this.theme();
        const themeConfig = { ...toThemeConfigPayload(current?.themeConfig), ...value.themeConfig };

        const freeformConfig: FreeformConfigPayload = { ...value.freeformConfig };
        const segmentsConfig = current?.freeformConfig?.segmentsConfig;
        if (segmentsConfig?.length) {
            // A loaded `description` is `null`; omit the key rather than sending null back.
            freeformConfig.segmentsConfig = segmentsConfig.map(segment => ({
                id: segment.id,
                label: segment.label,
                ...(segment.description ? { description: segment.description } : {}),
                displayOrder: segment.displayOrder
            }));
        }

        return { name: value.name, themeConfig, freeformConfig };
    }
}
